import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'CELPIPMax';
const SITE_URL = 'https://celpipmax.com';
const DEFAULT_DESC = 'Practice for your CELPIP exam with realistic mock tests, AI-powered scoring, and expert strategies. Achieve CLB 9+ with CELPIPMax.';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

export default function SEO({
  title,
  description = DEFAULT_DESC,
  path = '',
  type = 'website',
  image = DEFAULT_IMAGE,
  noIndex = false,
  jsonLd,
}) {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — #1 CELPIP Practice Test Platform`;
  const canonical = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// Pre-built JSON-LD schemas
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESC,
  sameAs: [],
};

export const courseSchema = (name, description) => ({
  '@context': 'https://schema.org',
  '@type': 'Course',
  name,
  description,
  provider: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
});

export const faqSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

export const blogPostSchema = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.excerpt,
  author: { '@type': 'Person', name: post.author_name || SITE_NAME },
  datePublished: post.published_at,
  publisher: { '@type': 'Organization', name: SITE_NAME },
  url: `${SITE_URL}/blog/${post.slug}`,
});
